// Service for the RL Optimization endpoints (/cms/rl-optimize/*) — a separate
// pipeline from /cms/optimize (processOptimizationService.js). See
// RL_OPTIMIZATION_FRONTEND_API.md for the full contract.
import { aiFetch } from "./aiServiceAuth";
const BASE_URL =
  process.env.REACT_APP_PROCESS_OPT_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_BASE_URL ||
  "";

function buildHeaders(extra = {}) {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    ...extra,
  };
}

function fetchWithTimeout(url, options, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return aiFetch(url, { ...options, signal: controller.signal })
    .then((res) => { clearTimeout(timer); return res; })
    .catch((err) => {
      clearTimeout(timer);
      if (err.name === "AbortError") throw new Error(`Request timed out after ${timeoutMs / 1000}s`);
      throw err;
    });
}

// `detail` is a string for 404s and some 400s, an object ({message, issues})
// for the rest (§10 of the API doc) — normalize both into one Error shape.
async function throwApiError(res) {
  let body = null;
  try { body = await res.json(); } catch (_) { }
  const detail = body?.detail;
  const message = typeof detail === "string" ? detail : (detail?.message || `HTTP ${res.status}`);
  const err = new Error(message);
  err.status = res.status;
  err.issues = (detail && typeof detail === "object" && detail.issues) || [];
  throw err;
}

async function getJson(path, timeoutMs = 30_000) {
  const res = await fetchWithTimeout(`${BASE_URL}${path}`, { method: "GET", headers: buildHeaders() }, timeoutMs);
  if (!res.ok) await throwApiError(res);
  return res.json();
}

async function postJson(path, body, timeoutMs = 30_000) {
  const res = await fetchWithTimeout(
    `${BASE_URL}${path}`,
    { method: "POST", headers: buildHeaders(), body: JSON.stringify(body || {}) },
    timeoutMs,
  );
  if (!res.ok) await throwApiError(res);
  return res.json();
}

// Poll the RL pipeline's OWN job registry — NOT the shared /cms/jobs/{id} that
// /cms/optimize uses (this pipeline keeps its own, per the API doc §8).
//
// The job_id is not just a polling handle: /save-optimized-version accepts it as
// ?rl_job_id to reuse THIS run's result instead of re-running the engine. RL is a
// stochastic search, so a re-run can legitimately return a different redesign from
// the one the analyst approved — see getRlOptimizationData's return shape.
async function pollRlJob(jobId, intervalMs = 2000) {
  const MAX_POLLS = 150; // ~5 minutes
  for (let i = 0; i < MAX_POLLS; i++) {
    const data = await getJson(`/cms/rl-optimize/jobs/${encodeURIComponent(jobId)}`);
    if (data.status === "done") return data.result;
    if (data.status === "error") {
      // Same detail shape as throwApiError above (§10 of the API doc): a
      // string for most failures, an object ({message, issues}) for others —
      // without this, `new Error(objectValue)` stringifies it to the useless
      // "[object Object]" instead of the actual failure reason.
      const detail = data.error;
      const message =
        typeof detail === "string"
          ? detail
          : (detail?.message || "RL optimization job failed on server");
      const err = new Error(message);
      err.issues = (detail && typeof detail === "object" && detail.issues) || [];
      throw err;
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new Error("RL optimization timed out after 5 minutes. Please try again.");
}

// Fast (~1s), no training — call before showing the goal picker to gate the UI
// on readiness (validation.issues[] when ready === false).
export async function getRlOptimizationPreview(processId) {
  if (!processId) throw new Error("processId is required");
  return getJson(`/cms/rl-optimize/${encodeURIComponent(processId)}/preview`);
}

// Runs as a background job (so slow/large processes don't hold a long request
// open) and polls until done. `goal`: "time" | "cost" | "both".
//
// `includeSubprocessRedesigns` opts into redesigning this process's sub-processes
// too and pricing their effect into the parent (off by default — it costs one extra
// training run per child). It MUST go in the body: this endpoint ignores
// ?include_subprocess_redesigns as a query param.
//
// Returns { result, job_id } rather than the bare result: the job_id is what
// /save-optimized-version needs to persist THIS run instead of training a fresh
// agent whose search may land somewhere else entirely.
export async function getRlOptimizationData(processId, options = {}) {
  if (!processId) throw new Error("processId is required");
  const {
    goal = "both", seed = 42, top_k = 3, episodes, time_priority, cost_priority,
    includeSubprocessRedesigns = false,
  } = options;
  const body = { goal, seed, top_k };
  if (episodes != null) body.episodes = episodes;
  if (time_priority != null) body.time_priority = time_priority;
  if (cost_priority != null) body.cost_priority = cost_priority;
  if (includeSubprocessRedesigns) body.include_subprocess_redesigns = true;
  const { job_id } = await postJson(`/cms/rl-optimize/${encodeURIComponent(processId)}/start`, body, 30_000);
  const result = await pollRlJob(job_id);
  return { result, job_id };
}
